import { WorkspaceCardViewers } from "features/data-management/workspace/workspace/body/projects-and-folders/grid-view/WorkspaceCardViewers"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { SearchResultComponent } from "../registry"
import { setNewViewstate } from "features/map/GlobalViewState"
import {
    PROJECT_POPUP_DIMENSIONS,
    selectProjectFilter,
    setActivePopup,
    setProjectFilter,
} from "features/overview/slice"
import {
    getProjectFilterOptionById,
    projectFilterOptions,
} from "features/overview/filters/projectFilterOptions"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useGeolocatedProjects } from "features/map/layers/geolocated-project/useGeolocatedProjects"
import { useMemo } from "react"
import { useRouter } from "pages"
import { useTimeAgo } from "@nova/toolkit-pure-hooks"
import { useLazyLoadedProjectThumbnail } from "tools/hooks/useLazyLoadedProjectThumbnail"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { LiveBadge } from "./LiveBadge"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { beginProjectLocationOverride } from "features/overview/location-override/thunks"
import { selectActiveContextMenu } from "features/popups/contextMenu"

type Props = SearchResultComponent<{ projectId: string }>

export const ProjectSearchResult = ({ projectId, onAfterSelect }: Props) => {
    const project = useAppSelector(selectFireById(projectId))
    const handleClick = useProjectSearchResultClickHandler({ projectId, onAfterSelect })

    if (!project) return null

    return (
        <div
            className="p-3 flex gap-3 items-center hover:bg-white-100 cursor-pointer"
            onClick={handleClick}
        >
            <div className="shrink-0 flex justify-center items-center">
                <div className="w-5 h-5 bg-blue-500/20 rounded-full flex justify-center items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                </div>
            </div>
            <div className="flex gap-2 items-center shrink min-w-0">
                <div className="text-white grow shrink min-w-0 truncate">{project.name}</div>
                {!!project.drones?.length && (
                    <LiveBadge
                        label={`${project.drones.length > 1 ? `${project.drones.length} ` : ""}Live`}
                    />
                )}
            </div>
        </div>
    )
}

export const ProjectFullPanelHeader = () => {
    const dispatch = useAppDispatch()
    const projectFilterId = useAppSelector(selectProjectFilter)
    const projectFilter = getProjectFilterOptionById(projectFilterId)
    return (
        <div className="pt-2 px-3 flex justify-between items-center">
            <div className="text-lg text-white telegraf">Projects</div>
            <div className="flex items-center gap-2">
                <small className="xs text-white-300">Active In</small>
                <Dropdown stopPropagation>
                    <Dropdown.Trigger className="h-full shrink-0">
                        <div className="h-full flex items-center group">
                            <div className="shrink-0 transition-colors text-gray-300 group-hover:text-gray-200">
                                {projectFilter.title}
                            </div>
                            <Icons.SmallIcon
                                Icon={Icons.ArrowDropdown}
                                className="transition-colors text-gray-300 group-hover:text-gray-100"
                            />
                        </div>
                    </Dropdown.Trigger>
                    <Dropdown.Menu>
                        <div className="py-1 bg-gray-700 flex flex-col w-[150px]">
                            {projectFilterOptions.map((option) => {
                                return (
                                    <Dropdown.ButtonItem
                                        key={option.id}
                                        onClick={() => dispatch(setProjectFilter(option.id))}
                                    >
                                        <div className="flex items-center h-7 py-1 cursor-pointer">
                                            <div className="w-11 shrink-0 flex justify-center items-center">
                                                {option.id === projectFilterId && (
                                                    <Icons.SmallIcon
                                                        Icon={Icons.Checkmark}
                                                        className="text-green-500"
                                                    />
                                                )}
                                            </div>
                                            <small className="text-white">{option.title}</small>
                                        </div>
                                    </Dropdown.ButtonItem>
                                )
                            })}
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    )
}

export const ProjectFullPanelResult = ({ projectId, onAfterSelect }: Props) => {
    const dispatch = useAppDispatch()
    const project = useAppSelector(selectFireById(projectId))
    const handleClick = useProjectSearchResultClickHandler({ projectId, onAfterSelect })
    const { blobUrl, containerRef, isFetching } = useLazyLoadedProjectThumbnail({ projectId })
    const updatedAgo = useTimeAgo({
        timestamp: project?.updatedAt ? Date.parse(project.updatedAt) : undefined,
    })
    const router = useRouter("/workspace/:workspaceId")

    const activeContextMenu = useAppSelector(selectActiveContextMenu("OverviewProject"))
    const isMenuOpen = activeContextMenu?.data.projectId === projectId

    if (!project) return null

    return (
        <div
            className="w-full py-2 pl-4 pr-5 flex gap-5 cursor-pointer overflow-hidden hover:bg-white-100 group"
            onClick={handleClick}
            onContextMenu={(e) => {
                e.preventDefault()
                e.stopPropagation()
                dispatch(
                    setActiveContextMenu({
                        type: "OverviewProject",
                        data: { projectId },
                        position: {
                            top: e.clientY,
                            left: e.clientX,
                        },
                    })
                )
            }}
        >
            <div className="shrink-0 w-[138px] h-[78px] rounded-md relative">
                <div ref={containerRef} className="absolute inset-0 rounded-lg overflow-hidden">
                    {blobUrl && (
                        <img
                            className="w-full h-full object-cover object-center"
                            src={blobUrl}
                            alt="project"
                        />
                    )}
                    {isFetching && (
                        <div className="w-full shimmer-loading-gradient overflow-hidden rounded-md" />
                    )}
                </div>
                <router.Link to={{ path: "/project/:projectId", params: { projectId } }}>
                    <div className="absolute inset-0 flex justify-center items-center gap-1 bg-black-700 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <div className="text-white">Open Project</div>
                        <Icons.SmallIcon Icon={Icons.ArrowRight} className="text-white" />
                    </div>
                </router.Link>
                <WorkspaceCardViewers
                    viewers={project.viewers}
                    drones={[]}
                    className="absolute -bottom-2.5 left-2.5 pointer-events-none"
                />
            </div>
            <div className="flex flex-col gap-1.5 justify-center min-w-0 grow">
                <div className="w-fit">
                    {project.drones?.length > 0 && (
                        <LiveBadge
                            label={
                                project.drones.length > 1
                                    ? `${project.drones.length} Live`
                                    : undefined
                            }
                        />
                    )}
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="min-w-0 truncate telegraf text-white text-base">
                        {project.name}
                    </div>
                    <small className="text-white-400">{updatedAgo}</small>
                </div>
            </div>
            {isMenuOpen && activeContextMenu && (
                <ContextMenu
                    sections={[
                        [
                            {
                                title: "Update location",
                                Icon: Icons.AddLocation,
                                onClick: (e) => {
                                    e.stopPropagation()
                                    dispatch(setActiveContextMenu(undefined))
                                    dispatch(beginProjectLocationOverride({ projectId }))
                                },
                            },
                        ],
                    ]}
                    onBlur={() => dispatch(setActiveContextMenu(undefined))}
                    isFixedPosition
                    top={activeContextMenu.position.top}
                    left={activeContextMenu.position.left}
                />
            )}
        </div>
    )
}

export const useProjectSearchResultClickHandler = ({ projectId, onAfterSelect }: Props) => {
    const dispatch = useAppDispatch()
    const project = useAppSelector(selectFireById(projectId))
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const {
        data: { geolocatedProjects },
    } = useGeolocatedProjects({ orgId })
    const hasMapNode = useMemo(
        () => geolocatedProjects.some((p) => p.id === projectId),
        [geolocatedProjects, projectId]
    )
    const router = useRouter("/workspace/:workspaceId")

    if (!project) return

    const handleClick = () => {
        const location =
            project.location?.longitude && project.location?.latitude
                ? {
                      longitude: project.location.longitude,
                      latitude: project.location.latitude,
                  }
                : undefined
        if (hasMapNode && location) {
            setNewViewstate({
                latitude: location.latitude,
                longitude: location.longitude,
                zoom: 12,
            })
            dispatch(
                setActivePopup({
                    id: project.id,
                    type: "project",
                    placementType: "point",
                    point: { lat: location.latitude, lng: location.longitude },
                    renderedDimensions: PROJECT_POPUP_DIMENSIONS,
                    margin: {
                        x: -PROJECT_POPUP_DIMENSIONS.width,
                        y: -PROJECT_POPUP_DIMENSIONS.height,
                    },
                })
            )
            onAfterSelect()
        } else {
            onAfterSelect()
            router.navigate({ path: "/project/:projectId", params: { projectId } })
        }
    }

    return handleClick
}
